import React, { useEffect, useState } from "react";
import { Search, Eye, X } from "lucide-react";
import axios from "axios";

import AdminLayout from "../components/AdminLayout";
import { BASE_URL } from "../../../config/config";
import { useNavigate } from "react-router-dom";

const Investors = () => {
  const [investors, setInvestors] = useState([]);
  const [search, setSearch] = useState("");
  const [kycTab, setKycTab] = useState("All");
  const [isLoading, setIsLoading] = useState(false);

  // Portfolio modal states
  const [isPortfolioModalOpen, setIsPortfolioModalOpen] = useState(false);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [portfolioError, setPortfolioError] = useState("");
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const navigate = useNavigate()

  const getInvestors = async () => {
    setIsLoading(true);

    const token = localStorage.getItem("rwatoken");

    try {
      const response = await axios.get(`${BASE_URL}/admin/investors`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Investors API response:", response);

      if (response?.data) {
        setInvestors(response.data);
      }
    } catch (error) {
      if(error?.status === 401){
        localStorage.removeItem("rwatoken");
        localStorage.removeItem("userRole");
        window.location.href = "/";
        navigate("/login", { replace: true });
      }
      console.log("Error fetching investors:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInvestors();
  }, []);

  // Get investor portfolio
  const getInvestorPortfolio = async (investor) => {
    setSelectedInvestor(investor);
    setIsPortfolioModalOpen(true);
    setPortfolioLoading(true);
    setPortfolioError("");
    setPortfolio(null);

    const token = localStorage.getItem("rwatoken");

    try {
      const response = await axios.get(
        `${BASE_URL}/admin/investors/${investor.id}/portfolio`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Investor portfolio response:", response);

      if (response?.data) {
        setPortfolio(response.data);
      }
    } catch (error) {
      console.log("Error fetching investor portfolio:", error);

      setPortfolioError(
        error?.response?.data?.message ||
          "Unable to load investor portfolio."
      );
    } finally {
      setPortfolioLoading(false);
    }
  };

  const closePortfolioModal = () => {
    setIsPortfolioModalOpen(false);
    setSelectedInvestor(null);
    setPortfolio(null);
    setPortfolioError("");
  };

  const filteredInvestors = investors.filter((investor) => {
    const fullName = `${investor.first_name || ""} ${
      investor.last_name || ""
    }`.trim();

    const searchValue = search.toLowerCase();

    const matchesSearch =
      fullName.toLowerCase().includes(searchValue) ||
      investor.email?.toLowerCase().includes(searchValue) ||
      investor.phone_number?.toLowerCase().includes(searchValue) ||
      investor.wallet_address?.toLowerCase().includes(searchValue);

    const matchesKyc =
      kycTab === "All" ||
      investor.kyc_status?.toLowerCase() === kycTab.toLowerCase();

    return matchesSearch && matchesKyc;
  });

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getKycStatus = (status) => {
    if (!status) return "Pending";

    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus.includes("approved")) {
      return "Approved";
    }

    if (normalizedStatus.includes("reject")) {
      return "Rejected";
    }

    if (normalizedStatus.includes("pending")) {
      return "Pending";
    }

    return status;
  };

  return (
    <AdminLayout title="Investors">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="text-[17px] font-semibold text-[#142b3b]">
            Investors
          </h1>

          <p className="mt-1 text-[9px] text-[#84909b]">
            View investor profiles, portfolios, and verification status.
          </p>
        </div>

        <span className="rounded-[6px] bg-[#d9f8e4] px-3 py-2 text-[10px] font-medium text-[#159447]">
          {investors.length} Investors
        </span>
      </div>

      <div className="rounded-[8px] border border-[#e0e4e8] bg-white">
        {/* Toolbar */}
        <div className="flex flex-col gap-2 border-b border-[#edf0f2] p-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex">
            {["All", "Pending", "Approved", "Rejected"].map((item) => (
              <button
                key={item}
                onClick={() => setKycTab(item)}
                className={`rounded-[5px] px-3 py-1.5 text-[10px] ${
                  kycTab === item
                    ? "bg-[#f2f5f8] font-medium text-[#1d3444]"
                    : "text-[#71808c]"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="relative w-full sm:max-w-[280px]">
            <Search
              size={12}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa4ad]"
            />

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search investors..."
              className="h-[30px] w-full rounded-[6px] border border-[#e0e4e8] pl-8 text-[10px] outline-none"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-[#e7eaed] bg-[#fafbfc]">
                {[
                  "Investor",
                  "Phone Number",
                  "Wallet Address",
                  "Email Verification",
                  "KYC Status",
                  "Created At",
                  "Action",
                ].map((heading) => (
                  <th
                    key={heading}
                    className="px-3 py-2.5 text-left text-[10px] font-medium text-[#71808c]"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-8 text-center text-[10px] text-[#84909b]"
                  >
                    Loading investors...
                  </td>
                </tr>
              ) : filteredInvestors.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-8 text-center text-[10px] text-[#84909b]"
                  >
                    No investors found
                  </td>
                </tr>
              ) : (
                filteredInvestors.map((investor) => {
                  const fullName =
                    `${investor.first_name || ""} ${
                      investor.last_name || ""
                    }`.trim() || "Unnamed Investor";

                  const initials = `${investor.first_name?.[0] || ""}${
                    investor.last_name?.[0] || ""
                  }`.toUpperCase();

                  const kycStatus = getKycStatus(investor.kyc_status);

                  return (
                    <tr
                      key={investor.id}
                      className="border-b border-[#edf0f2] last:border-0"
                    >
                      {/* Investor */}
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full bg-[#dce9ff] text-[8px] font-medium text-[#285ce7]">
                            {initials || "IN"}
                          </div>

                          <div>
                            <p className="text-[10px] font-medium text-[#243846]">
                              {fullName}
                            </p>

                            <p className="text-[10px] text-[#9aa4ad]">
                              {investor.email || "-"}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Phone */}
                      <td className="px-3 text-[10px] text-[#354858]">
                        {investor.phone_number || "-"}
                      </td>

                      {/* Wallet */}
                      <td className="px-3 text-[10px] text-[#354858]">
                        <span
                          className="block max-w-[180px] truncate"
                          title={investor.wallet_address}
                        >
                          {investor.wallet_address || "-"}
                        </span>
                      </td>

                      {/* Email Verification */}
                      <td className="px-3">
                        <VerificationBadge
                          verified={investor.email_verified}
                        />
                      </td>

                      {/* KYC Status */}
                      <td className="px-3">
                        <KycBadge status={kycStatus} />
                      </td>

                      {/* Created At */}
                      <td className="px-3 text-[10px] text-[#354858]">
                        {formatDate(investor.created_at)}
                      </td>

                      {/* Action */}
                      <td className="px-3">
                        <button
                          onClick={() => getInvestorPortfolio(investor)}
                          className="flex items-center gap-1.5 rounded-[5px] border border-[#dfe4e8] px-2.5 py-1.5 text-[10px] font-medium text-[#243846] transition hover:bg-[#f5f7f9]"
                        >
                          <Eye size={12} />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Portfolio Modal */}
      {isPortfolioModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={closePortfolioModal}
        >
          <div
            className="w-full max-w-[800px] overflow-hidden rounded-[10px] bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-[#edf0f2] px-5 py-4">
              <div>
                <h2 className="text-[15px] font-semibold text-[#142b3b]">
                  Investor Portfolio
                </h2>

                {selectedInvestor && (
                  <p className="mt-1 text-[10px] text-[#84909b]">
                    {selectedInvestor.first_name}{" "}
                    {selectedInvestor.last_name}
                  </p>
                )}
              </div>

              <button
                onClick={closePortfolioModal}
                className="flex h-7 w-7 items-center justify-center rounded-full text-[#71808c] transition hover:bg-[#f2f5f8]"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="max-h-[500px] overflow-y-auto p-5">
              {portfolioLoading ? (
                <div className="flex min-h-[180px] items-center justify-center">
                  <p className="text-[11px] text-[#84909b]">
                    Loading portfolio...
                  </p>
                </div>
              ) : portfolioError ? (
                <div className="flex min-h-[180px] items-center justify-center">
                  <p className="text-[11px] text-[#df3d3d]">
                    {portfolioError}
                  </p>
                </div>
              ) : !portfolio?.holdings?.length ? (
                <div className="flex min-h-[180px] items-center justify-center">
                  <p className="text-[11px] text-[#84909b]">
                    No portfolio holdings found.
                  </p>
                </div>
              ) : (
                <>
                  {/* Portfolio Summary */}
                  <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <div className="rounded-[8px] border border-[#e7eaed] bg-[#fafbfc] p-3">
                      <p className="text-[9px] text-[#84909b]">
                        Investor ID
                      </p>

                      <p className="mt-1 text-[12px] font-semibold text-[#243846]">
                        {portfolio.investor_id}
                      </p>
                    </div>

                    <div className="rounded-[8px] border border-[#e7eaed] bg-[#fafbfc] p-3">
                      <p className="text-[9px] text-[#84909b]">
                        Properties
                      </p>

                      <p className="mt-1 text-[12px] font-semibold text-[#243846]">
                        {portfolio.holdings.length}
                      </p>
                    </div>

                    <div className="rounded-[8px] border border-[#e7eaed] bg-[#fafbfc] p-3">
                      <p className="text-[9px] text-[#84909b]">
                        Total Tokens
                      </p>

                      <p className="mt-1 text-[12px] font-semibold text-[#243846]">
                        {portfolio.holdings.reduce(
                          (total, holding) =>
                            total + Number(holding.blocks_held || 0),
                          0
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Holdings Table */}
                  <div className="overflow-x-auto rounded-[8px] border border-[#e0e4e8]">
                    <table className="w-full min-w-[650px]">
                      <thead>
                        <tr className="border-b border-[#e7eaed] bg-[#fafbfc]">
                          {[
                            "Property",
                            "Tokens Held",
                            "Ownership",
                            "Rent Earned",
                          ].map((heading) => (
                            <th
                              key={heading}
                              className="px-3 py-2.5 text-left text-[10px] font-medium text-[#71808c]"
                            >
                              {heading}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {portfolio.holdings.map((holding) => (
                          <tr
                            key={holding.property_id}
                            className="border-b border-[#edf0f2] last:border-0"
                          >
                            <td className="px-3 py-3">
                              <div>
                                <p className="text-[10px] font-medium text-[#243846]">
                                  {holding.property_name || "-"}
                                </p>

                                <p className="mt-0.5 text-[9px] text-[#9aa4ad]">
                                  Property ID: {holding.property_id}
                                </p>
                              </div>
                            </td>

                            <td className="px-3 text-[10px] text-[#354858]">
                              {holding.blocks_held ?? 0}
                            </td>

                            <td className="px-3 text-[10px] font-medium text-[#159447]">
                              {holding.ownership_pct ?? 0}%
                            </td>

                            <td className="px-3 text-[10px] font-medium text-[#263a48]">
                              AED{" "}
                              {Number(
                                holding.rent_earned_aed || 0
                              ).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end border-t border-[#edf0f2] px-5 py-3">
              <button
                onClick={closePortfolioModal}
                className="rounded-[6px] bg-[#142b3b] px-4 py-2 text-[10px] font-medium text-white transition hover:bg-[#1d4055]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const VerificationBadge = ({ verified }) => {
  return (
    <span
      className={`rounded-full px-2 py-1 text-[10px] ${
        verified
          ? "bg-[#d9f8e4] text-[#159447]"
          : "bg-[#fff1c7] text-[#bd7b00]"
      }`}
    >
      {verified ? "Verified" : "Pending"}
    </span>
  );
};

const KycBadge = ({ status }) => {
  const styles = {
    Approved: "bg-[#d9f8e4] text-[#159447]",
    Pending: "bg-[#fff1c7] text-[#bd7b00]",
    Rejected: "bg-[#ffe0e0] text-[#df3d3d]",
  };

  return (
    <span
      className={`rounded-full px-2 py-1 text-[10px] ${
        styles[status] || "bg-[#f2f5f8] text-[#71808c]"
      }`}
    >
      {status || "Pending"}
    </span>
  );
};

export default Investors;
